import React, { useEffect, useState } from "react";
import { Download, DownloadCloud, FolderOpen, FileText } from "lucide-react";
import { PageHead, Loader, EmptyState } from "../components/PageHead";
import { Badge } from "../components/Badges";
import { fmtDate } from "../lib/format";
import api, { apiErr, API } from "../lib/api";
import { toast } from "sonner";

const CAT_LABELS = {
  bee: ["BEE Certificate", "b-gold"],
  insurance: ["Insurance", "b-blue"],
  compliance: ["Compliance", "b-violet"],
  policy: ["Policy", "b-slate"],
  other: ["Document", "b-slate"],
};

export default function PortalDocuments() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/portal/documents").then((r) => setRows(r.data)).catch((e) => { toast.error(apiErr(e)); setRows([]); });
  }, []);

  const dlOne = async (doc) => {
    try {
      const r = await api.get(`/portal/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = doc.file_name || doc.title || "document";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const dlAll = async () => {
    try {
      const token = localStorage.getItem("pte_token");
      const resp = await fetch(`${API}/portal/documents/download-all`, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "PTE-Documents.zip";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message || apiErr(e)); }
  };

  return (
    <>
      <PageHead crumb="My Account" title="Documents"
        sub="Download PTE's published documents — BEE certificate, insurance letters, compliance papers and more."
        actions={rows && rows.length > 0 && (
          <button className="btn gold" onClick={dlAll} data-testid="portal-doc-zip"><DownloadCloud size={15} /> Download all (.zip)</button>
        )} />

      {rows === null && <Loader />}
      {rows && rows.length === 0 && (
        <EmptyState icon={FolderOpen}>No documents have been published yet.</EmptyState>
      )}

      {rows && rows.length > 0 && (
        <div style={{ display: "grid", gap: 12 }}>
          {rows.map((d) => {
            const [label, cls] = CAT_LABELS[d.category] || CAT_LABELS.other;
            return (
              <div key={d.id} className="card pad" data-testid={`portal-doc-${d.id}`}
                style={{ display: "flex", gap: 14, alignItems: "center" }}>
                <div style={{ width: 44, height: 44, background: "var(--card-2)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--gold-deep)" }}>
                  <FileText size={22} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span className="t-strong" style={{ fontSize: 15 }}>{d.title}</span>
                    <Badge label={label} cls={cls} />
                    {d.valid_until && <span className="s t-mut">· valid until {d.valid_until}</span>}
                  </div>
                  {d.description && <div className="s t-mut" style={{ marginTop: 3 }}>{d.description}</div>}
                  <div className="s t-mut mono" style={{ marginTop: 4, fontSize: 11.5 }}>{d.file_name} · {fmtDate(d.created_at)}</div>
                </div>
                <button className="btn ghost" onClick={() => dlOne(d)} data-testid={`portal-doc-dl-${d.id}`}><Download size={14} /> View / Download</button>
              </div>
            );
          })}
        </div>
      )}
    </>
  );
}
